/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * Error Boundary — catches render errors and shows fallback UI.
 *
 * Auto-recovers when the user navigates to a different route so a single
 * broken page doesn't force a full refresh — previously any page crash
 * stuck the entire app on "Something went wrong" until the user reloaded.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { reportError } from '@utils/errorReporter';

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    reportError(error, {
      source: 'ErrorBoundary',
      routeKey: this.props.routeKey,
      componentStack: errorInfo?.componentStack,
    });
  }

  componentDidUpdate(prevProps) {
    // When the route key changes (passed down by the functional wrapper),
    // reset so the user can navigate away from the broken page.
    if (this.state.hasError && prevProps.routeKey !== this.props.routeKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="d-flex align-items-center justify-content-center min-vh-100">
          <div className="text-center p-5">
            <div style={{ fontSize: '4rem' }}>😵</div>
            <h2 className="mt-3">Something went wrong</h2>
            <p className="text-muted">This page hit an unexpected error. Try navigating to another section or refresh the page.</p>
            <div className="d-flex gap-2 justify-content-center">
              <button className="btn btn-outline-primary" onClick={this.props.onGoBack}>
                <i className="bi bi-arrow-left me-1"></i>Go Back
              </button>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                <i className="bi bi-arrow-clockwise me-1"></i>Refresh Page
              </button>
            </div>
            {(import.meta.env?.DEV || (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development')) && this.state.error && (
              <pre className="text-start mt-4 p-3 bg-light rounded small text-danger" style={{ maxHeight: 200, overflow: 'auto' }}>
                {this.state.error.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ErrorBoundary = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <ErrorBoundaryInner routeKey={location.pathname} onGoBack={() => navigate(-1)}>
      {children}
    </ErrorBoundaryInner>
  );
};

export default ErrorBoundary;
